import { ClerkProvider } from "@clerk/clerk-react";
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import App, { LocalPreview } from "./App";
import "./styles.css";

interface PublicConfig {
  clerkPublishableKey: string;
  environment: string;
}

function Root() {
  const localPreviewHosts = new Set(["127.0.0.1", "localhost", "::1"]);
  // ローカル確認専用。/marketing?preview=1 や /access?preview=1 で、ログインなしに見本のデータで表示する。
  const isLocalPreview = import.meta.env.DEV
    && localPreviewHosts.has(location.hostname)
    && (location.pathname.startsWith("/marketing") || location.pathname === "/access")
    && new URLSearchParams(location.search).has("preview");
  const [previewReady, setPreviewReady] = useState(false);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLocalPreview) return;
    void import("./dev/preview-fixtures").then(({ installPreviewFixtures }) => {
      installPreviewFixtures();
      setPreviewReady(true);
    });
  }, [isLocalPreview]);

  useEffect(() => {
    if (isLocalPreview) return;
    fetch("/api/public-config")
      .then(async (response) => {
        if (!response.ok) throw new Error("設定の取得に失敗しました");
        return response.json() as Promise<PublicConfig>;
      })
      .then(setConfig)
      .catch((reason: Error) => setError(reason.message));
  }, [isLocalPreview]);

  if (isLocalPreview) {
    return previewReady ? <LocalPreview initialPage={location.pathname === "/access" ? "access" : "marketing"} /> : null;
  }

  if (error) return <div className="center-state"><p>{error}</p></div>;
  if (!config) return <div className="center-state"><div className="spinner" aria-label="読み込み中" /></div>;
  if (!config.clerkPublishableKey) {
    return <div className="center-state"><p>Clerkの公開キーが未設定です。</p></div>;
  }

  return (
    <ClerkProvider publishableKey={config.clerkPublishableKey}>
      <App />
    </ClerkProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
